// Mapping of Acxiom vendor-locked codes (e.g., AI, AN, BF) to internal property names.
// The raw keys (e.g., AI, AN) come directly from Acxiom and should not be changed.
// The mapped keys (e.g., a_age, a_haschildren) follow our internal naming convention (prefix 'a_' for Acxiom traits).

import { readCookie, createCookieByMins } from 'modules/cookieManager';
import { fromEvent } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import mapAcxiomTrackingProperties from './acxiomLabelMapper';

const storage = window.sessionStorage;
const acxiomDomain = 'https://ri.ax5.co';

/**
 * Saves the Acxiom data to sessionStorage under the key 'ridata'.
 * @param {Object} acxiomData - The Acxiom data to be stored.
 */
const saveDataToSessionStorage = (acxiomData) => {
  storage.setItem('ridata', acxiomData);
};

/**
 * Saves the Acxiom data to a cookie named 'ri_data'.
 * @param {Object} acxiomData - The Acxiom data to be stored.
 */
const saveDataToCookieStorage = (acxiomData) => {
  const serialized = encodeURIComponent(acxiomData);
  createCookieByMins('ri_data', serialized);
};

/**
 * Retrieves the raw Acxiom data from sessionStorage or cookies.
 * Attempts to get the 'ridata' value from sessionStorage first.
 * If not found, it looks for the 'ri_data' cookie in the document's cookies.
 * @returns {string|null} The raw Acxiom data as a string if found, otherwise null.
 */
function getRawAcxiomData() {
  const sessionData = storage.getItem('ridata');
  if (sessionData) {
    return JSON.parse(sessionData);
  }

  const cookieData = readCookie('ri_data');
  if (cookieData) {
    const decodedCookieData = decodeURIComponent(cookieData);
    return JSON.parse(decodedCookieData);
  }

  return null;
}

/**
 * Retrieves Acxiom tracking data from sessionStorage or cookies.
 * Prefers sessionStorage ('ridata') over the 'ri_data' cookie if both exist.
 * @returns {Object|null} - The decoded Acxiom data object, or null if not found or invalid.
 */
const getAcxiomTrackingProperties = () => {
  const rawData = getRawAcxiomData();
  const trackingProperties = mapAcxiomTrackingProperties(rawData);
  return trackingProperties;
};

/**
 * Observable that listens for a single 'message' event from the window object,
 * filters messages to only those originating from 'https://ri.ax5.co' and containing a 'ri_response' property,
 * and emits the 'ri_response' data from the event.
 * Note: The Acxiom third-party script may send the string 'undefined' when no data is available.
 */
const acxiomMessage$ = fromEvent(window, 'message').pipe(
  filter((event) => event.origin === acxiomDomain),
  map((event) => event.data.ri_response),
  filter((data) => data && data !== 'undefined'),
  take(1)
);

const acxiomSubscription = acxiomMessage$.subscribe((acxiomData) => {
  saveDataToSessionStorage(acxiomData);
  saveDataToCookieStorage(acxiomData);
  acxiomSubscription.unsubscribe();

  document.dispatchEvent(new CustomEvent('acxiom-data-saved'));
});

export default getAcxiomTrackingProperties;